import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MaterialsDesktop } from "./materials/MaterialsDesktop";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { supabase } from "../supabase";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import {
  archiveMaterials,
  buildMaterialCatalogWrite,
  loadMaterialsCatalog,
} from "../helpers/materialsCatalog";
import {
  insertScopedMaterial,
  updateScopedMaterial,
} from "../helpers/operationalQueryAdapters";
import {
  MATERIAL_ADMIN_COLUMNS,
  MATERIAL_SEARCH_COLUMNS,
  getMaterialRelationName,
  stripMaterialPricingFromList,
} from "../helpers/materialVisibility";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { findHubBatchAction } from "../helpers/hubBatchSelection";
import {
  MATERIALS_HUB_BATCH_ACTIONS,
  getMaterialsHubBatchMode,
} from "../helpers/materialsHubBatchSelection";
import { useBatchSelection } from "../hooks/useBatchSelection";
import { resolveOrganizationIdForQueries } from "../helpers/orgQueryScope";
import {
  isMissingBucketError,
  isMissingImageSupportError,
  removeMaterialImage,
  uploadMaterialImage,
} from "../helpers/materialImages";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import {
  CP_MODAL_OVERLAY_STYLE,
  CP_MODAL_PANEL_STYLE,
} from "./shared/crewpilotModalStyles";
import { ViewportPortal } from "./shared/ViewportDialog";
import { MaterialDetails } from "./materials/MaterialDetails";
import { MaterialEditor } from "./materials/MaterialEditor";
import { RecIcon } from "./materials/MaterialRecordParts";
import { MaterialRow } from "./materials/MaterialRow";
import { MaterialTile } from "./materials/MaterialTile";
import { useMaterialImageUrls } from "./materials/useMaterialImageUrls";
import {
  MATERIALS_VIEW,
  readMaterialsViewPreference,
  writeMaterialsViewPreference,
} from "./materials/materialsViewPreference";
import {
  MATERIAL_SORT,
  MaterialsActionBar,
  MaterialsFilterChip,
  MaterialsSelectionBar,
  MaterialsViewToggle,
} from "./materials/MaterialsToolbar";
import {
  TYPE_FILTER_ALL,
  hasLoadedField,
  matchesTypeFilter,
  typeFilterOptions,
} from "./materials/materialRecordModel";
import "./materials/materialRecord.css";

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = CP_MODAL_PANEL_STYLE;

function getMaterialItemId(material) {
  return material.material_id;
}

function costSortValue(material) {
  const value = Number(material?.unit_cost);
  return material?.unit_cost == null || material?.unit_cost === "" || !Number.isFinite(value)
    ? null
    : value;
}

function sortMaterials(rows, sortKey) {
  const byName = (a, b) =>
    String(a.material_name || "").localeCompare(String(b.material_name || ""), undefined, {
      sensitivity: "base",
    });

  if (sortKey === MATERIAL_SORT.NAME) {
    return [...rows].sort(byName);
  }

  const direction = sortKey === MATERIAL_SORT.COST_DESC ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = costSortValue(a);
    const right = costSortValue(b);
    // Unknown costs always sort last, in either direction.
    if (left == null && right == null) return byName(a, b);
    if (left == null) return 1;
    if (right == null) return -1;
    return left === right ? byName(a, b) : (left - right) * direction;
  });
}

export function MaterialsScreen({ onBack, initialView = "list" }) {
  const isDesktopViewport = useIsDesktopViewport();
  const { can, actor, orgPolicies } = usePermissions();
  const canEditCatalog = can(CAPABILITY.MATERIAL_CATALOG_EDIT);
  const canViewPricing = can(CAPABILITY.MATERIAL_CATALOG_VIEW_PRICING);

  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // view: { type: "list" } | { type: "details", material } | { type: "edit", material|null }
  const [view, setView] = useState({ type: "list" });
  const createLaunchRef = useRef(false);

  useEffect(() => {
    if (createLaunchRef.current) return;
    if (initialView === "create" && canEditCatalog) {
      createLaunchRef.current = true;
      setView({ type: "edit", material: null });
    }
  }, [initialView, canEditCatalog]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteConfirmIds, setBatchDeleteConfirmIds] = useState(null);
  const [hubError, setHubError] = useState("");
  const [selecting, setSelecting] = useState(false);
  const [sortKey, setSortKey] = useState(MATERIAL_SORT.NAME);
  const [typeKey, setTypeKey] = useState(TYPE_FILTER_ALL);
  const [catalogView, setCatalogView] = useState(readMaterialsViewPreference);
  // Photos need the material-images migration; until then behave exactly as before.
  const [imagesSupported, setImagesSupported] = useState(true);

  const batchMode = getMaterialsHubBatchMode();
  const batchSelectionEnabled = Boolean(batchMode) && canViewPricing;

  function applyMaterialRows(rows) {
    const safeRows = canViewPricing
      ? rows || []
      : stripMaterialPricingFromList(rows || []);
    setMaterials(safeRows);
  }

  async function loadMaterials() {
    if (shouldSkipSupabase(actor)) {
      setMaterials([]);
      return [];
    }

    // Pricing screens must request pricing columns explicitly: the shared
    // field catalog is pricing-free, which previously made unit_cost undefined
    // ($NaN) and let the next save overwrite the stored cost with 0.
    const baseColumns = canViewPricing
      ? [...new Set([...MATERIAL_ADMIN_COLUMNS, "material_type"])]
      : MATERIAL_SEARCH_COLUMNS;
    const relation = getMaterialRelationName(actor, orgPolicies);
    const request = (columns) =>
      loadMaterialsCatalog({
        onCached: canViewPricing ? undefined : applyMaterialRows,
        relation,
        actor,
        columns,
      });

    let result = await request(
      imagesSupported ? [...baseColumns, "image_path"] : baseColumns,
    );

    // Environment without the material-images migration yet: keep working.
    if (result.error && imagesSupported && isMissingImageSupportError(result.error)) {
      setImagesSupported(false);
      result = await request(baseColumns);
    }

    const { materials: rows, error } = result;

    if (error) {
      console.error(error);
    }

    if (rows) {
      applyMaterialRows(rows);
    }
    return rows || [];
  }

  function changeCatalogView(next) {
    setCatalogView(next);
    writeMaterialsViewPreference(next);
  }

  function openAddMaterial() {
    if (!canEditCatalog) {
      return;
    }
    setView({ type: "edit", material: null });
  }

  function openDetails(material) {
    setView({ type: "details", material });
  }

  function describePhotoError(error) {
    if (isMissingBucketError(error) || isMissingImageSupportError(error)) {
      return "Photos aren't enabled in this environment yet.";
    }
    return error?.message || "The photo couldn't be saved.";
  }

  async function submitMaterial(form, imageChange = null) {
    if (blockDemoSave(actor)) {
      return { error: "Demo Mode: sign in to save your work." };
    }

    if (!canEditCatalog) {
      return { error: "You do not have permission to edit materials." };
    }

    const existing = view.type === "edit" ? view.material : null;
    const built = buildMaterialCatalogWrite(form, {
      pricingKnown: !existing || hasLoadedField(existing, "unit_cost"),
      inventoryKnown: !existing || hasLoadedField(existing, "current_inventory"),
      typeKnown: !existing || hasLoadedField(existing, "material_type"),
    });

    if (built.error) {
      return { error: built.error };
    }

    const payload = built.value;
    const wantsImage = imagesSupported && imageChange;
    const organizationId = wantsImage
      ? await resolveOrganizationIdForQueries(actor, supabase)
      : null;
    if (wantsImage && !organizationId) {
      return { error: "Your organization could not be resolved, so the photo can't be saved." };
    }

    let materialId = existing?.material_id;
    let photoNotice = "";
    let removedPhoto = false;

    if (existing) {
      // EDIT: upload first (the row already exists), then one update carries the new path.
      if (wantsImage && imageChange.type === "set") {
        try {
          payload.image_path = await uploadMaterialImage({
            organizationId,
            materialId,
            blob: imageChange.blob,
          });
        } catch (error) {
          return { error: describePhotoError(error) };
        }
      } else if (wantsImage && imageChange.type === "remove") {
        payload.image_path = null;
        removedPhoto = true;
      }

      const { error } = await updateScopedMaterial(supabase, actor, materialId, payload);
      if (error) {
        console.error(error);
        return { error: error.message || "Unable to save this material." };
      }
    } else {
      // CREATE: the object path needs the new id, and storage policy requires the
      // row to exist, so insert -> upload -> set image_path.
      const { data, error } = await insertScopedMaterial(supabase, actor, payload);
      if (error) {
        console.error(error);
        return { error: error.message || "Unable to save this material." };
      }
      materialId = data?.material_id;

      if (wantsImage && imageChange.type === "set" && materialId) {
        try {
          const path = await uploadMaterialImage({
            organizationId,
            materialId,
            blob: imageChange.blob,
          });
          const { error: linkError } = await updateScopedMaterial(supabase, actor, materialId, {
            image_path: path,
          });
          if (linkError) throw linkError;
        } catch (photoError) {
          console.error(photoError);
          // The material itself is saved; never leave a stray object behind.
          await removeMaterialImage({ organizationId, materialId });
          photoNotice = `Saved. ${describePhotoError(photoError)} Tap Edit to add the photo again.`;
        }
      }
    }

    if (removedPhoto) {
      // Row no longer references the object; delete the one file.
      const { error: removeError } = await removeMaterialImage({ organizationId, materialId });
      if (removeError) console.warn("[materials] image object removal failed", removeError);
    }

    const rows = await loadMaterials();

    if (wantsImage && imageChange.type === "set" && materialId && organizationId) {
      // Stable path => force a fresh signed URL so the new image shows at once.
      const savedRow = rows.find((row) => row.material_id === materialId);
      if (savedRow?.image_path) await images.refresh([savedRow.image_path]);
    }

    if (existing) {
      // Inactive records drop out of the active catalog; keep showing the
      // record the user just saved instead of losing it.
      const fresh =
        rows.find((row) => row.material_id === existing.material_id) ||
        { ...existing, ...payload };
      setView({ type: "details", material: fresh });
    } else if (photoNotice) {
      const created = rows.find((row) => row.material_id === materialId);
      setView(
        created
          ? { type: "details", material: created, notice: photoNotice }
          : { type: "list" },
      );
    } else {
      setView({ type: "list" });
    }

    return {};
  }

  useEffect(() => {
    loadMaterials();
  }, [actor, orgPolicies, canViewPricing]);

  // Memoized: useBatchSelection resets its state whenever this array's identity
  // changes, so a fresh array every render looped forever for users without
  // pricing access (batch selection disabled).
  const images = useMaterialImageUrls(materials);

  // Real material_type values only; any value present in the data gets an option.
  const typeOptions = useMemo(() => typeFilterOptions(materials), [materials]);
  const activeTypeOption = typeOptions.find((option) => option.key === typeKey);

  // Pipeline: type filter -> search (within the type) -> sort.
  const filteredMaterials = useMemo(() => {
    const search = searchTerm.toLowerCase();

    const matches = materials.filter(
      (material) =>
        matchesTypeFilter(material, typeKey) &&
        (material.material_name?.toLowerCase().includes(search) ||
          material.sku?.toLowerCase().includes(search) ||
          material.description?.toLowerCase().includes(search)),
    );
    return sortMaterials(matches, canViewPricing ? sortKey : MATERIAL_SORT.NAME);
  }, [materials, searchTerm, sortKey, typeKey, canViewPricing]);

  // Selection is an explicit mode: leaving it clears whatever was selected.
  const selectionActive = batchSelectionEnabled && selecting;
  const batchSelection = useBatchSelection(filteredMaterials, getMaterialItemId, {
    enabled: selectionActive,
  });

  function exitSelectionMode() {
    batchSelection.clearSelection();
    setSelecting(false);
  }

  const handleBatchAction = useCallback(
    (actionId) => {
      if (!batchMode || !batchSelectionEnabled) return;

      const action = findHubBatchAction(batchMode, actionId);
      if (!action) return;

      if (actionId === MATERIALS_HUB_BATCH_ACTIONS.IMPORT_CSV) {
        setHubError("");
        alert("CSV import is coming soon.");
        return;
      }

      const selectedIds = batchSelection.getSelectedIds();
      if (selectedIds.length === 0) return;

      if (actionId === MATERIALS_HUB_BATCH_ACTIONS.DELETE_SELECTED) {
        if (!canEditCatalog) return;
        setBatchDeleteConfirmIds(selectedIds);
      }
    },
    [batchMode, batchSelection, batchSelectionEnabled, canEditCatalog],
  );

  async function handleConfirmBatchDelete() {
    if (!batchDeleteConfirmIds?.length || !canEditCatalog) return;

    if (blockDemoSave(actor)) {
      return;
    }

    setBatchDeleting(true);
    setHubError("");
    try {
      const { error } = await archiveMaterials(batchDeleteConfirmIds, supabase, actor);
      if (error) {
        throw error;
      }

      batchSelection.clearSelection();
      setSelecting(false);
      setBatchDeleteConfirmIds(null);
      await loadMaterials();
    } catch (error) {
      setHubError(error.message || "Unable to archive selected materials.");
    } finally {
      setBatchDeleting(false);
    }
  }

  const goBack = useCallback(() => {
    setView((current) => {
      if (current.type === "edit" && current.material) {
        return { type: "details", material: current.material };
      }
      return { type: "list" };
    });
  }, []);

  // Device Back steps out of selection mode, then details/edit, before leaving Materials.
  useHardwareBackHandler(() => {
    if (view.type === "list" && selecting) {
      exitSelectionMode();
      return true;
    }
    goBack();
    return true;
  }, view.type !== "list" || selecting);

  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [view.type]);

  if (view.type === "details") {
    return (
      <MaterialDetails
        material={view.material}
        canEdit={canEditCatalog}
        canViewPricing={canViewPricing}
        imageUrl={images.urlFor(view.material)}
        notice={view.notice}
        onBack={goBack}
        onEdit={() => setView({ type: "edit", material: view.material })}
      />
    );
  }

  if (view.type === "edit" && canEditCatalog) {
    return (
      <MaterialEditor
        key={view.material?.material_id || "new"}
        material={view.material}
        canViewPricing={canViewPricing}
        imageUrl={view.material ? images.urlFor(view.material) : undefined}
        photosEnabled={imagesSupported}
        onCancel={goBack}
        onSubmit={submitMaterial}
      />
    );
  }

  if (isDesktopViewport) {
    return (
      <MaterialsDesktop
        subtitle={
          canEditCatalog
            ? "Inventory management and job costing"
            : canViewPricing
              ? "Browse the material catalog with pricing"
              : "Search the material catalog (pricing hidden)"
        }
        onBack={onBack}
        materials={materials}
        filtered={filteredMaterials}
        typeOptions={typeOptions}
        typeKey={typeKey}
        onTypeChange={setTypeKey}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        sortKey={sortKey}
        onSortChange={setSortKey}
        canViewPricing={canViewPricing}
        canEditCatalog={canEditCatalog}
        showImport={batchSelectionEnabled}
        showSelect={batchSelectionEnabled && canEditCatalog}
        selecting={selectionActive}
        selectedCount={batchSelection.selectedCount}
        allSelected={batchSelection.allSelected}
        isSelected={batchSelection.isSelected}
        onToggleItem={batchSelection.toggleItem}
        onToggleAll={batchSelection.toggleAll}
        onExitSelection={exitSelectionMode}
        onStartSelection={() => setSelecting(true)}
        onDeleteSelected={() => handleBatchAction(MATERIALS_HUB_BATCH_ACTIONS.DELETE_SELECTED)}
        batchBusy={batchDeleting}
        onImport={() => handleBatchAction(MATERIALS_HUB_BATCH_ACTIONS.IMPORT_CSV)}
        onAdd={openAddMaterial}
        onOpen={openDetails}
        onEdit={
          canEditCatalog ? (material) => setView({ type: "edit", material }) : undefined
        }
        catalogView={catalogView}
        onCatalogView={changeCatalogView}
        imageUrlFor={images.urlFor}
        hubError={hubError}
      >
        {batchDeleteConfirmIds && (
          <ViewportPortal>
          <div style={MODAL_OVERLAY_STYLE}>
            <div style={MODAL_PANEL_STYLE}>
              <h3 style={{ marginTop: 0 }}>Archive Selected Materials?</h3>
              <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
                {batchDeleteConfirmIds.length} material
                {batchDeleteConfirmIds.length === 1 ? "" : "s"} will be removed
                from the active catalog. You can restore them later by reactivating
                in the edit form.
              </p>
              <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
                <button
                  type="button"
                  className="nx-btn"
                  style={{ flex: 1 }}
                  onClick={() => setBatchDeleteConfirmIds(null)}
                  disabled={batchDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nx-btn"
                  style={{ flex: 1, borderColor: "rgba(224,107,90,0.45)", color: "#e06b5a" }}
                  onClick={handleConfirmBatchDelete}
                  disabled={batchDeleting}
                >
                  {batchDeleting ? "Archiving…" : "Delete Selected"}
                </button>
              </div>
            </div>
          </div>
          </ViewportPortal>
        )}
      </MaterialsDesktop>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Materials"
      subtitle={
        canEditCatalog
          ? "Inventory management and job costing"
          : canViewPricing
            ? "Browse the material catalog with pricing"
            : "Search the material catalog (pricing hidden)"
      }
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
    >
      <div className="mat-searchrow">
        <div className="mat-searchbox">
          <RecIcon name="search" />
          <input
            type="search"
            className="mat-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search materials..."
            aria-label="Search materials"
          />
        </div>
        <MaterialsViewToggle view={catalogView} onChange={changeCatalogView} />
      </div>

      {selectionActive ? (
        <MaterialsSelectionBar
          count={batchSelection.selectedCount}
          allSelected={batchSelection.allSelected}
          onSelectAll={batchSelection.toggleAll}
          onDelete={() => handleBatchAction(MATERIALS_HUB_BATCH_ACTIONS.DELETE_SELECTED)}
          onCancel={exitSelectionMode}
          busy={batchDeleting}
          canDelete={canEditCatalog}
        />
      ) : (
        <MaterialsActionBar
          canAdd={canEditCatalog}
          showImport={batchSelectionEnabled}
          showSelect={batchSelectionEnabled && canEditCatalog}
          onAdd={openAddMaterial}
          onImport={() => handleBatchAction(MATERIALS_HUB_BATCH_ACTIONS.IMPORT_CSV)}
          onSelect={() => setSelecting(true)}
          sortKey={sortKey}
          onSortChange={setSortKey}
          canSortByCost={canViewPricing}
          typeOptions={typeOptions}
          typeKey={typeKey}
          onTypeChange={setTypeKey}
        />
      )}

      {typeKey !== TYPE_FILTER_ALL && (
        <MaterialsFilterChip
          label={activeTypeOption?.label || "Filtered"}
          onClear={() => setTypeKey(TYPE_FILTER_ALL)}
        />
      )}

      {hubError && (
        <div style={{ color: "#e06b5a", marginBottom: "10px" }}>{hubError}</div>
      )}

      {filteredMaterials.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            color: "var(--nx-text-muted, #5b6472)",
          }}
        >
          {typeKey !== TYPE_FILTER_ALL
            ? `No ${activeTypeOption?.label || "matching"} records found.`
            : "No materials found."}
          {typeKey !== TYPE_FILTER_ALL && (
            <button
              type="button"
              className="mat-empty-clear"
              onClick={() => setTypeKey(TYPE_FILTER_ALL)}
            >
              Show All Types
            </button>
          )}
        </div>
      ) : (
        <div className={catalogView === MATERIALS_VIEW.THUMBNAIL ? "mat-grid" : "mat-list"}>
          {filteredMaterials.map((material) => {
            const Item = catalogView === MATERIALS_VIEW.THUMBNAIL ? MaterialTile : MaterialRow;
            return (
              <Item
                key={material.material_id}
                material={material}
                canViewPricing={canViewPricing}
                imageUrl={images.urlFor(material)}
                selecting={selectionActive}
                selected={batchSelection.isSelected(material.material_id)}
                onToggle={() => batchSelection.toggleItem(material.material_id)}
                onOpen={openDetails}
              />
            );
          })}
        </div>
      )}

      {batchDeleteConfirmIds && (
        <ViewportPortal>
        <div style={MODAL_OVERLAY_STYLE}>
          <div style={MODAL_PANEL_STYLE}>
            <h3 style={{ marginTop: 0 }}>Archive Selected Materials?</h3>
            <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
              {batchDeleteConfirmIds.length} material
              {batchDeleteConfirmIds.length === 1 ? "" : "s"} will be removed
              from the active catalog. You can restore them later by reactivating
              in the edit form.
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => setBatchDeleteConfirmIds(null)}
                disabled={batchDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nx-btn"
                style={{
                  flex: 1,
                  borderColor: "rgba(224,107,90,0.45)",
                  color: "#e06b5a",
                }}
                onClick={handleConfirmBatchDelete}
                disabled={batchDeleting}
              >
                {batchDeleting ? "Archiving…" : "Delete Selected"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      )}
    </CrewPilotModuleShell>
  );
}
